import { EventEmitter } from 'events';

interface WebSocketMessage {
  type: string;
  data: any;
}

class WebSocketService {
  private socket: WebSocket | null = null;
  private eventEmitter: EventEmitter;
  private reconnectInterval: number = 5000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private wsUrl: string;
  private isConnected: boolean = false;
  private maxReconnectAttempts: number = 10;
  private reconnectAttempts: number = 0;
  private destroyed: boolean = false;
  private _listenerMap: Map<(...args: any[]) => void, (...args: any[]) => void> = new Map();

  constructor(wsUrl: string = `${typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss' : 'ws'}://${typeof window !== 'undefined' ? window.location.host : 'localhost'}/ws`) {
    this.eventEmitter = new EventEmitter();
    // Configuration pour éviter les erreurs non gérées
    this.eventEmitter.setMaxListeners(20);
    this.wsUrl = wsUrl;
    
    // Gestion de la fermeture de l'onglet/navigateur
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.disconnect(true);
      });
    }
  }

  public connect(): void {
    if (this.destroyed) {
      // WebSocketService destroyed
      return;
    }

    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      // Already connected
      return;
    }

    // Connecting...
    try {
      // Inclure le token JWT en query param pour l'authentification WebSocket
      const token = localStorage.getItem('auth_token');
      const wsUrlWithToken = token ? `${this.wsUrl}${this.wsUrl.includes('?') ? '&' : '?'}token=${token}` : this.wsUrl;
      this.socket = new WebSocket(wsUrlWithToken);

      this.socket.onopen = () => {
        // Connected
        this.isConnected = true;
        this.reconnectAttempts = 0; // Réinitialiser le compteur de tentatives
        
        try {
          this.eventEmitter.emit('connected');
        } catch (error) {
          // Error in handler
        }
        
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.socket.onmessage = (event: MessageEvent) => {
        try {
          // Validation du message avant parsing
          if (!event.data) {
            // Empty message
            return;
          }
          
          const message: WebSocketMessage = JSON.parse(event.data);
          
          // Vérifier que le message a la bonne structure
          if (!message || typeof message.type !== 'string') {
            // Invalid format
            return;
          }
          
          try {
            this.eventEmitter.emit('message', message);
            
            // Émettre également un événement spécifique pour le type de message
            if (message.type) {
              this.eventEmitter.emit(message.type, message.data);
            }
          } catch (error) {
            // Handler error
          }
        } catch (error) {
          // Parse error
        }
      };

      this.socket.onerror = (error) => {
        // WS error
        try {
          // Émettre l'événement d'erreur pour les abonnés
          this.eventEmitter.emit('error', { message: 'WebSocket connection error', originalError: error });
        } catch (emitError) {
          // Emit error
        }
        
        // Ne pas reconnecter immédiatement en cas d'erreur - attendre l'événement onclose
        // La reconnexion sera gérée par onclose
      };

      this.socket.onclose = (event) => {
        // Connection closed
        this.isConnected = false;
        
        // Si la connexion a été fermée normalement (code 1000), ne pas reconnecter
        const wasCleanDisconnect = event.code === 1000 && event.reason === 'user_disconnected';
        
        try {
          this.eventEmitter.emit('disconnected', { wasClean: wasCleanDisconnect });
        } catch (error) {
          // Disconnected handler error
        }
        
        // Ne pas reconnecter si c'est une déconnexion propre demandée par l'utilisateur
        // ou si le service a été détruit
        if (!wasCleanDisconnect && !this.destroyed) {
          this.reconnect();
        }
      };
    } catch (error) {
      // Connection error
      this.reconnect();
    }
  }

  public disconnect(fromUnload: boolean = false): void {
    if (this.socket) {
      try {
        // Fermer proprement avec code 1000 et raison pour indiquer que c'est une déconnexion voulue
        this.socket.close(1000, fromUnload ? 'page_unload' : 'user_disconnected');
      } catch (error) {
        // Close error
      }
      
      if (!fromUnload) {
        // Ne pas logger en cas de fermeture du navigateur (inutile)
        // Disconnected by client
      }
      
      this.socket = null;
      this.isConnected = false;
    } 
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private reconnect(): void {
    if (this.destroyed) {
      // WebSocketService destroyed
      return;
    }

    // Incrémenter le compteur de tentatives
    this.reconnectAttempts++;

    // Vérifier si on a dépassé le nombre maximum de tentatives
    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      // Max attempts reached — schedule auto-reset after 5 minutes
      this.destroyed = true;
      try {
        this.eventEmitter.emit('max_reconnect_attempts');
        this.eventEmitter.emit('connectionLost');
      } catch (error) {
        // Max attempts handler error
      }
      // Auto-reset after 5 minutes to allow reconnection
      setTimeout(() => {
        this.reset();
      }, 5 * 60 * 1000);
      return;
    }

    // Augmenter progressivement l'intervalle de reconnexion (backoff exponentiel)
    const delay = Math.min(30000, this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1));
    
    // Reconnecting...

    // Planifier une reconnexion après l'intervalle calculé
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  public send(type: string, data: any): boolean {
    if (this.destroyed) {
      // Service destroyed
      return false;
    }
    
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        const message: WebSocketMessage = {
          type,
          data
        };
        this.socket.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        return false;
      }
    }
    return false;
  }
  
  public reset(): void {
    this.destroyed = false;
    this.reconnectAttempts = 0;
    this.connect();
  }

  public destroy(): void {
    this.destroyed = true;
    this.disconnect();
    this.eventEmitter.removeAllListeners();
  }

  public on(event: string, listener: (...args: any[]) => void): () => void {
    // Envelopper le listener dans un try/catch pour éviter les erreurs non gérées
    const safeListener = (...args: any[]) => {
      try {
        listener(...args);
      } catch (error) {
        console.error(`Error in event listener for '${event}':`, error);
      }
    };
    
    // Stocker le mapping original → safe pour permettre off() par référence originale
    if (!this._listenerMap) {
      this._listenerMap = new Map();
    }
    this._listenerMap.set(listener, safeListener);
    
    this.eventEmitter.on(event, safeListener);
    
    // Retourner une fonction pour retirer l'écouteur
    return () => {
      this.eventEmitter.off(event, safeListener);
      this._listenerMap?.delete(listener);
    };
  }

  public off(event: string, listener: (...args: any[]) => void): void {
    const safeListener = this._listenerMap?.get(listener);
    if (safeListener) {
      this.eventEmitter.off(event, safeListener);
      this._listenerMap?.delete(listener);
    } else {
      this.eventEmitter.off(event, listener);
    }
  }

  public isConnectedToServer(): boolean {
    return this.isConnected;
  }
}

// Exporter une instance singleton
export const webSocketService = new WebSocketService();

// Exporter également la classe pour les tests ou autres utilisations
export default WebSocketService;

